/**
 * Hook for managing the watchlist with IndexedDB persistence.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWatchlist,
  saveWatchlistItem,
  removeWatchlistItem,
  getWatchlistItem,
} from '@/lib/stockDb';
import type { WatchlistItem } from '@/types/stock';

export function useWatchlist() {
  return useQuery({
    queryKey: ['watchlist'],
    queryFn: () => getWatchlist(),
  });
}

export function useWatchlistItem(ticker: string) {
  return useQuery({
    queryKey: ['watchlist', ticker.toUpperCase()],
    queryFn: () => getWatchlistItem(ticker.toUpperCase()),
    enabled: !!ticker,
  });
}

export function useAddToWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: WatchlistItem) => {
      await saveWatchlistItem(item);
      return item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
    },
  });
}

export function useRemoveFromWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ticker: string) => {
      await removeWatchlistItem(ticker.toUpperCase());
      return ticker;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
    },
  });
}
